import { useCallback, useEffect, useRef, type MutableRefObject } from "react";
import type { PreOrder } from "@/app/api/types/preOrder";

interface PendingWaiter {
  id: string;
  resolve: (found: boolean) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Returns a function that resolves once a target pre-order id appears in the
 * supplied `preOrders` array, or `false` after `timeoutMs` if it never does.
 *
 * Generalizes the ad-hoc `pendingHighlightRef` pattern from RecapHeader so
 * any caller (the unified `useGoToPreOrder` hook) can sequence:
 *   - apply state mutations (date, filters, …)
 *   - await arrival in data
 *   - then highlight + scroll
 *
 * Resolves immediately if the id is already present.
 */
export function useWaitForPreOrderInData(
  preOrders: ReadonlyArray<Pick<PreOrder, "id">>,
): (id: string, timeoutMs?: number) => Promise<boolean> {
  const preOrdersRef = useRef(preOrders);
  preOrdersRef.current = preOrders;

  const waitersRef = useRef<PendingWaiter[]>([]);

  // On every data update, resolve any waiter whose target now appears.
  useEffect(() => {
    if (waitersRef.current.length === 0) return;
    const stillPending: PendingWaiter[] = [];
    for (const w of waitersRef.current) {
      const found = preOrders.some((po) => po.id === w.id);
      if (found) {
        clearTimeout(w.timer);
        w.resolve(true);
      } else {
        stillPending.push(w);
      }
    }
    waitersRef.current = stillPending;
  }, [preOrders]);

  // Cancel everything on unmount.
  useEffect(() => {
    return () => {
      for (const w of waitersRef.current) {
        clearTimeout(w.timer);
        w.resolve(false);
      }
      waitersRef.current = [];
    };
  }, []);

  return useCallback(
    (id: string, timeoutMs: number = 1500): Promise<boolean> => {
      if (preOrdersRef.current.some((po) => po.id === id)) {
        return Promise.resolve(true);
      }
      return enqueueWaiter(waitersRef, id, timeoutMs);
    },
    [],
  );
}

function enqueueWaiter(
  waitersRef: MutableRefObject<PendingWaiter[]>,
  id: string,
  timeoutMs: number,
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => {
      waitersRef.current = waitersRef.current.filter((w) => w.timer !== timer);
      resolve(false);
    }, timeoutMs);
    waitersRef.current.push({ id, resolve, timer });
  });
}
